import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useAuth } from "context/AuthContext"
import { useTRPCClient } from "utils/trpc"

type BaseFavorite = {
  id: string
}

export type ProjectFavorite = BaseFavorite & {
  type: "project"
  data: {
    name: string | null
    href: string
    logo: string | null
    symbol: string | null
  }
}

export type DashboardFavorite = BaseFavorite & {
  type: "dashboard"
  data: {
    name: string | null
    description: string | null
    href: string
    logo: string | null
    isOwner: boolean
  }
}

export type ChartFavorite = BaseFavorite & {
  type: "chart"
  data: {
    name: string | null
    href: string
    logo: string | null
    isOwner: boolean
  }
}

export type Favorite = DashboardFavorite | ProjectFavorite | ChartFavorite

const orderFavoritesByType = (favorites: Favorite[]) => {
  return [...favorites].sort((a, b) => {
    const typeOrder = {
      project: 0,
      dashboard: 1,
      chart: 2,
    }
    return typeOrder[a.type] - typeOrder[b.type]
  })
}

/**
 * A top-level hook for managing all types of favorites via local storage.
 * @todo Should move this db persistence so it sticks with a user across browsers.
 */

export type UseFavoritesReturn = {
  favorites: Favorite[]
  setFavorites: (newFavorites: Favorite[]) => void
  isLoading: boolean
}

type FavoritesResponse = {
  favorites: Favorite[]
}

export const useFavorites = (): UseFavoritesReturn => {
  const { isLoggedIn } = useAuth()
  const trpcClient = useTRPCClient()
  const queryClient = useQueryClient()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["favorites"],
    queryFn: async () => {
      const response = await trpcClient.users.getOrgUserFavorites.query(
        undefined,
        {
          context: {
            skipBatch: true,
          },
        }
      )
      return response as FavoritesResponse
    },
  })

  const favorites = data?.favorites ?? []

  const { mutate: setFavorites } = useMutation({
    mutationFn: (newFavorites: Favorite[]) => {
      const orderedFavorites = orderFavoritesByType(newFavorites)
      return trpcClient.users.postOrgUserFavorites.mutate({
        favorites: orderedFavorites,
      })
    },
    onMutate: async (newFavorites) => {
      await queryClient.cancelQueries({ queryKey: ["favorites"] })

      const previousFavorites = queryClient.getQueryData(["favorites"])
      const orderedFavorites = orderFavoritesByType(newFavorites)

      // Optimistically update to the new value
      queryClient.setQueryData(["favorites"], { favorites: orderedFavorites })

      // Return a context object with the snapshotted value
      return { previousFavorites }
    },
    onError: (err, newFavorites, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(["favorites"], context?.previousFavorites)
    },
    onSettled: () => {
      // Always refetch after error or success
      refetch()
    },
  })

  return {
    favorites: isLoggedIn ? favorites : [],
    setFavorites,
    isLoading,
  }
}
